import { useEffect, useId } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ProtectedInformation } from "@/assets/onboarding/protected-information";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackNamePageViewed } from "@/features/analytics/trackers/name-page-viewed";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { User } from "@/shared/domains/users";
import {
  Button,
  Form,
  Input,
  Layout,
  SectionHeader,
  UserIcon,
} from "@/shared/ui";
import { LONGEVITY_ROUTE } from "../router/constants";

export const AccountPersonalDetails = () => {
  const { analytics } = useCustomerIoContext();
  const personalDetails = useOnboardingStore((state) => state.personalDetails);
  const setPersonalDetails = useOnboardingStore(
    (state) => state.setPersonalDetails,
  );

  const formId = useId();
  const navigate = useNavigate();

  useEffect(() => {
    trackNamePageViewed({ customerIoAnalytics: analytics });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    resolver: zodResolver(User.omit({ email: true })),
    mode: "onTouched",
    defaultValues: personalDetails,
  });

  const handleNext = form.handleSubmit((values) => {
    setPersonalDetails(values);
    navigate(LONGEVITY_ROUTE.ACCOUNT_CONTACT_DETAILS);
  });

  return (
    <Layout.Content>
      <SectionHeader title={t`Let's get started`} />

      <Form.Provider {...form}>
        <form
          id={formId}
          onSubmit={handleNext}
          className="flex w-full flex-col gap-2 sm:max-w-xl"
        >
          <Form.Field
            control={form.control}
            name="firstName"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`First name`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("firstName")}
                    placeholder={t`E.g. John`}
                  >
                    <Input.Slot>
                      <UserIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <Form.Field
            control={form.control}
            name="lastName"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`Last name`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("lastName")}
                    placeholder={t`E.g. Doe`}
                  >
                    <Input.Slot>
                      <UserIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <Button size="lg" type="submit">
            {t`Continue`}
          </Button>
        </form>
      </Form.Provider>

      <ProtectedInformation />
    </Layout.Content>
  );
};
